import React, { useState, useEffect } from 'react';
import Header from '../components/header';
import UpdateStockDetails from './updateStockDetails';
import './ccmUpdateHistory.css';
import { formatCycleInfoDate, getCalendarInfo, parseAndNormalizeDate, parseAndNormalizeEndOfDay } from '../helper-functions';
import { get } from '../config/api';
import { CCM_UPDATE_HISTORY_TITLES, CCM_UPDATE_HISTORY_MESSAGES } from '../constants/textconstants';
import { RequestStockData } from '../interfaces/types';


interface HistoryItem {
  stockCycle: string;
  totalAvailableStock: number;
  status?: string;
  customerName?: string;
  customerId?: string;
  stockData?: RequestStockData[];
  productStocks?: any[];
}

// interface RequestStockData {
//   productName: string;
//   productDisplayName?: string;
//   stockQuantity: number;
// }

interface CCMUpdateHistoryProps {
  historyData?: HistoryItem[];
  onBack?: () => void;
  onNavigateToHome?: () => void;
  onNavigateToHistory?: (historyData: any[], retailerInfo: any) => void;
  userDetails?: { id: string; name: string; };
  selectedItem?: any;
  onRefreshAndNavigateHome?: () => void; // Add new prop for refresh + navigation
  productsData?: any[]; // Add productsData prop for enriching product display names
}

export const CCMUpdateHistory: React.FC<CCMUpdateHistoryProps> = ({
  historyData = [],
  onBack,
  onNavigateToHome,
  onNavigateToHistory,
  userDetails,
  selectedItem,
  onRefreshAndNavigateHome,
  productsData = []
}) => {
  const [selectedEntry, setSelectedEntry] = useState<HistoryItem | null>(null);
  const [showStockDetails, setShowStockDetails] = useState<boolean>(false);
  const [showEditStockDetails, setShowEditStockDetails] = useState<boolean>(false);
  const [stockData, setStockData] = useState<RequestStockData[]>([]);
  const [isLoadingStockData, setIsLoadingStockData] = useState<boolean>(false);

  // Access calendarInfo from localStorage
  const calendarInfo = getCalendarInfo();

  // Calculate total quantity for stock details view
  const stockDetailsTotalQuantity = stockData.reduce((sum: number, item: RequestStockData) => sum + item.stockQuantity, 0);

  const handleBackClick = () => {
    if (showEditStockDetails) {
      setShowEditStockDetails(false);
    } else if (showStockDetails) {
      setShowStockDetails(false);
      setSelectedEntry(null);
      setStockData([]);
    } else if (onBack) {
      onBack();
    } else {
      console.log('Back to CCM Home');
    }
  };

  const handleHistoryItemClick = async (item: HistoryItem, index: number) => {
    setShowStockDetails(true);
    setIsLoadingStockData(true);
    
    // Clear previous stock data
    setStockData([]);
    setSelectedEntry(null);
    
    try {
      // Fetch data from api/v1/customers/customerID/product-stocks based on customerId and stockCycle
      const customerId = item.customerId;
      const stockCycle = item.stockCycle;
      
      if (!customerId) {
        console.warn('No customer ID available for API fetch');
        setSelectedEntry(item);
        setStockData([]);
        return;
      }

      console.log('Fetching product stocks for customer:', customerId, 'with stockCycle:', stockCycle);

      // Call the specific API endpoint for customer product stocks
      const response = await get(`/api/v1/customers/${customerId}/product-stocks`);
      console.log('Customer product stocks API response:', response);
      
      if (response && (Array.isArray(response) ? response.length > 0 : response)) {
        // Handle the response data
        const historyData = Array.isArray(response) ? response : [response];
        
        // Filter based on stockCycle if provided
        const filteredHistory = stockCycle 
          ? historyData.filter((historyItem: any) => 
              historyItem.stockCycle === stockCycle ||
              historyItem.cycleDate === stockCycle
            )
          : historyData;

        // Use the first matching item or the first item if no filter match
        const selectedApiData = filteredHistory.length > 0 ? filteredHistory[0] : historyData[0];
        
        if (selectedApiData) {
          console.log('Selected API data:', selectedApiData);
          
          // Create the selected entry from API data
          const apiSelectedEntry: HistoryItem = {
            stockCycle: selectedApiData.stockCycle || selectedApiData.cycleDate || stockCycle || '',
            totalAvailableStock: selectedApiData.totalAvailableStock || item.totalAvailableStock || 0,
            status: selectedApiData.status || item.status || 'Unknown',
            customerName: item.customerName,
            customerId: customerId,
            stockData: selectedApiData.productStocks?.map((product: any) => {
              // Find matching product from productsData to get productDisplayName
              const matchingProduct = productsData.find(p => p.productName === product.productName);
              return {
                productName: product.productName || 'Unknown Product',
                productDisplayName: product.productDisplayName || matchingProduct?.productDisplayName || product.productName,
                stockQuantity: product.stockQuantity || 0
              };
            }) || [],
            productStocks: selectedApiData.productStocks || []
          };
          
          setSelectedEntry(apiSelectedEntry);
          
          // Extract stock data from the API response
          if (selectedApiData.productStocks && selectedApiData.productStocks.length > 0) {
            const extractedStockData: RequestStockData[] = selectedApiData.productStocks.map((product: any) => {
              // Find matching product from productsData to get productDisplayName
              const matchingProduct = productsData.find(p => p.productName === product.productName);
              return {
                productName: product.productName || 'Unknown Product',
                productDisplayName: product.productDisplayName || matchingProduct?.productDisplayName || product.productName,
                stockQuantity: product.stockQuantity || 0
              };
            });
            console.log('Extracted stock data from API:', extractedStockData);
            setStockData(extractedStockData);
          } else {
            console.log('No productStocks found in API response');
            setStockData([]);
          }
        } else {
          console.warn('No matching data found in API response');
          setSelectedEntry(item);
          setStockData([]);
        }
      } else {
        console.warn('No data returned from API for customer:', customerId);
        // Fallback to original item data
        setSelectedEntry(item);
        
        // Try to use existing stock data from item if available
        if (item.stockData && item.stockData.length > 0) {
          setStockData(item.stockData);
        } else if (item.productStocks && item.productStocks.length > 0) {
          const extractedStockData: RequestStockData[] = item.productStocks.map((product: any) => {
            // Find matching product from productsData to get productDisplayName
            const matchingProduct = productsData.find(p => p.productName === product.productName);
            return {
              productName: product.productName || 'Unknown Product',
              productDisplayName: product.productDisplayName || matchingProduct?.productDisplayName || product.productName,
              stockQuantity: product.stockQuantity || 0
            };
          });
          setStockData(extractedStockData);
        } else {
          setStockData([]);
        }
      }
    } catch (error) {
      console.error('Error fetching customer product stocks:', error);
      
      // Fallback to original item data on error
      setSelectedEntry(item);
      
      // Try to use existing stock data from item if available
      if (item.stockData && item.stockData.length > 0) {
        setStockData(item.stockData);
      } else if (item.productStocks && item.productStocks.length > 0) {
        const extractedStockData: RequestStockData[] = item.productStocks.map((product: any) => {
          // Find matching product from productsData to get productDisplayName
          const matchingProduct = productsData.find(p => p.productName === product.productName);
          return {
            productName: product.productName || 'Unknown Product',
            productDisplayName: product.productDisplayName || matchingProduct?.productDisplayName || product.productName,
            stockQuantity: product.stockQuantity || 0
          };
        });
        setStockData(extractedStockData);
      } else {
        setStockData([]);
      }
    } finally {
      setIsLoadingStockData(false);
    }
  };

  // Auto-navigate to selected item when component mounts or selectedItem changes
  useEffect(() => {
    if (selectedItem) {
      // Find the index of the selected item in historyData
      const itemIndex = historyData.findIndex(item => 
        item.customerId === selectedItem.customerId && 
        item.stockCycle === selectedItem.stockCycle
      );
      
      if (itemIndex !== -1) {
        // Automatically click on the selected item
        handleHistoryItemClick(selectedItem, itemIndex);
      }
    }
  }, [selectedItem, historyData]);

  const handleEditStock = () => {
    console.log('Edit stock for date:', selectedEntry?.stockCycle);
    setShowEditStockDetails(true);
  };

  const handleSaveStockDetails = (updatedStockData: any[]) => {
    console.log('Stock details saved:', updatedStockData);
    
    // Update the local stockData state
    setStockData(updatedStockData);
    
    // Update the selected entry with the new stock data
    if (selectedEntry) {
      const updatedEntry = {
        ...selectedEntry,
        stockData: updatedStockData,
      };
      setSelectedEntry(updatedEntry);
    }
    
    setShowEditStockDetails(false);
  };
console.log(showStockDetails ,selectedEntry)
  return (
    <div >
      {showEditStockDetails ? (
        // Edit Stock Details View
        <UpdateStockDetails
          stockData={stockData.map(item => ({
            productName: item.productName,
            productDisplayName: item.productDisplayName || item.productName,
            stockQuantity: item.stockQuantity
          }))}
          selectedDate={selectedEntry?.stockCycle}
          onBack={() => setShowEditStockDetails(false)}
          onSave={handleSaveStockDetails}
          onNavigateToHome={onNavigateToHome}
          onNavigateToCCMHome={() => {
            // Navigate back to CCM home screen with refresh
            console.log('Navigating back to CCM home with refresh...');
            if (onRefreshAndNavigateHome) {
              onRefreshAndNavigateHome();
            } else if (onNavigateToHome) {
              onNavigateToHome();
            }
          }}
          userRole="ccm"
          customerId={selectedEntry?.customerId}
        />
      ) : (
        <div className="page-container ccm-update-history-container">
          {/* Header Section */}
          <Header 
            title={
              showStockDetails
                ? `${formatCycleInfoDate(selectedEntry?.stockCycle, 'MMM DD')} ${CCM_UPDATE_HISTORY_TITLES.DETAILS_SUFFIX}`
                : CCM_UPDATE_HISTORY_TITLES.UPDATE_BY_YOU
            }
            onBackClick={handleBackClick} 
          />

          {/* Retailer Info Section */}
          {showStockDetails && selectedEntry && (
            <div className="retailer-info-section">
              <div className="retailer-info-card">
                <div className="retailer-info-header">
                  <span className="retailer-info-name">
                    {selectedEntry.customerName}
                  </span>
                </div>
                <div className="retailer-info-id">
                  ID: {selectedEntry.customerId}
                </div>
              </div>
            </div>
          )}

          <div className="ccm-update-history-content">
            {showStockDetails ? (
              <>
                <div className="stat-label-list">
                  Stock as of{" "}
                  {`${formatCycleInfoDate(selectedEntry?.stockCycle, 'MMM DD, YYYY')}`}
                </div>
                
                {/* Stock Details View */}
                <div className="stock-details-view">
                  {isLoadingStockData ? (
                    <div className="loading-state">
                      <span className="loading-icon">⏳</span>
                      <p className="loading-text">{CCM_UPDATE_HISTORY_MESSAGES.LOADING_STOCK_DETAILS}</p>
                    </div>
                  ) : stockData.length === 0 ? (
                    <div className="empty-state">
                      <span className="empty-icon">📦</span>
                      <p className="empty-text">{CCM_UPDATE_HISTORY_MESSAGES.NO_STOCK_DETAILS_AVAILABLE} {selectedEntry?.stockCycle}</p>
                    </div>
                  ) : (
                    <>
                      <div className="stock-details-list">
                        {stockData.map((item: RequestStockData, index: number) => (
                          <div key={item.productName} className="stock-detail-item">
                            <span className="stock-detail-id">{item.productDisplayName || item.productName}</span>
                            <span className="stock-detail-quantity">
                              {item.stockQuantity} {CCM_UPDATE_HISTORY_MESSAGES.KGS}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="stock-total">
                        <span>{CCM_UPDATE_HISTORY_MESSAGES.TOTAL}</span>
                        <span>{stockDetailsTotalQuantity} {CCM_UPDATE_HISTORY_MESSAGES.KGS}</span>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              // History List View
              <div className="history-list-section">
                <div className="history-items-list">
                  {historyData.length === 0 ? (
                    <div className="no-history-message">
                      {/* <p>No update history available.</p> */}
                    </div>
                  ) : (
                    historyData.map((item, index) => (
                      <div 
                        key={index} 
                        className="history-item-card"
                        onClick={() => handleHistoryItemClick(item, index)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="item-header">
                            <h4 className="company-name">{item.customerName || CCM_UPDATE_HISTORY_MESSAGES.RETAILER}</h4>
                            <div className="quantity-info">
                              <span className="quantity-value">{item.totalAvailableStock} {CCM_UPDATE_HISTORY_MESSAGES.KGS}</span>
                            </div>
                        </div>
                        
                        <div className="item-footer">
                          <p className="company-type">{CCM_UPDATE_HISTORY_MESSAGES.ID_PREFIX} {item.customerId || CCM_UPDATE_HISTORY_MESSAGES.N_A}</p>
                          <div className="item-actions">
                            <div className="update-date">
                              <span className="date-text"> {formatCycleInfoDate(
                                                item.stockCycle,
                                                "DD MMM YYYY"
                                              )}</span>
                            </div>
                          </div>
                        </div>

                        {(() => {
                          const shouldShowAlert = () => {
                            // if (index !== 0) return false;
                            
                            const currentCycleStartDate = calendarInfo?.currentCycleStartDate ? new Date(calendarInfo.currentCycleStartDate) : null;
                            const currentCycleDueDate = calendarInfo?.currentCycleDueDate ? new Date(calendarInfo.currentCycleDueDate) : null;
                            
                            // Check if item's stockCycle matches current cycle start date
                            const itemDate = item.stockCycle ? new Date(item.stockCycle) : null;
                            const isCurrentCycle = currentCycleStartDate && itemDate && 
                                                 currentCycleStartDate.toDateString() === itemDate.toDateString();
                            
                            // Only show alert if this item is for the current stock cycle
                            if (!isCurrentCycle) return false;
                            
                            // For CCM: show if index=0 and currentCycleStartDate <= currentCycleDueDate
                            return currentCycleStartDate && currentCycleDueDate && 
                                   currentCycleStartDate <= currentCycleDueDate &&
                                   parseAndNormalizeDate(currentCycleStartDate?.toISOString() || '') <= new Date() &&
                                   new Date() <= parseAndNormalizeEndOfDay(currentCycleDueDate?.toISOString() || '')
                          };
                          
                          return shouldShowAlert() && (
                            <div className="history-update-alert">
                              <span className="history-alert-icon">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M11 7H13V9H11V7ZM11 11H13V17H11V11ZM12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
                                    fill="#0095A5"
                                  />
                                </svg>
                              </span>
                              <span className="history-alert-text">
                                {CCM_UPDATE_HISTORY_MESSAGES.YOU_CAN_ADD_EDIT_CCM} {formatCycleInfoDate(calendarInfo?.currentCycleDueDate, 'DD MMM YYYY')}                              </span>
                            </div>
                          );
                        })()}
                </div>
              ))
            )}
          </div>

                {historyData.length === 0 && (
                  <div className="empty-state">
                    <span className="empty-icon">📋</span>
                    <p className="empty-message">{CCM_UPDATE_HISTORY_MESSAGES.NO_UPDATE_HISTORY_FOUND}</p>
                    <p className="empty-subtitle">{CCM_UPDATE_HISTORY_MESSAGES.TRY_ADJUSTING_SEARCH}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer with Edit Stock button - show based on user role and cycle dates */}
          {(() => {
            const shouldShowEditFooter = () => {
              if (!showStockDetails || !selectedEntry) return false;
              
              // For debugging - let's be more lenient with the index check for now
              // and just check if we have data
              if (historyData.length === 0) return false;
              
              const currentCycleStartDate = calendarInfo?.currentCycleStartDate ? new Date(calendarInfo.currentCycleStartDate) : null;
              const currentCycleDueDate = calendarInfo?.currentCycleDueDate ? new Date(calendarInfo.currentCycleDueDate) : null;
              
              // Check if selected entry's stockCycle matches current cycle start date
              const entryDate = selectedEntry?.stockCycle ? new Date(selectedEntry.stockCycle) : null;
              const isCurrentCycle = currentCycleStartDate && entryDate && 
                                   currentCycleStartDate.toDateString() === entryDate.toDateString();
              
              // Only show edit footer if this entry is for the current stock cycle
              if (!isCurrentCycle) return false;
              
              // For CCM: show if index=0 and currentCycleStartDate <= currentCycleDueDate
              return currentCycleStartDate && currentCycleDueDate && 
                    parseAndNormalizeDate(currentCycleStartDate?.toISOString() || '') <= new Date() &&
                    new Date() <= parseAndNormalizeEndOfDay(currentCycleDueDate?.toISOString() || '')
            };
            
            return shouldShowEditFooter() && (
              <div className="edit-stock-footer">
                <button className="edit-stock-btn" onClick={handleEditStock}>
                  {CCM_UPDATE_HISTORY_TITLES.EDIT_STOCK_DETAILS}
                </button>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default CCMUpdateHistory;
